import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

export const saveCompany = createAsyncThunk('savedCompanies/saveCompany', async ({ userId, companyId, organizationId = '' }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token")

        const res = await api.post(`/api/savedcompanies/savecompany`,
            { userId, companyId, organizationId },
            {
                headers: {
                    "auth-token": token
                }
            }
        );

        return res.data;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error saving company');
    }
}
);

export const saveMultipleCompanies = createAsyncThunk('savedCompanies/saveMultipleCompanies', async ({ userId, companyIds, organizationIds }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const res = await api.post(`/api/savedcompanies/savemultiple`,
            { userId, companyIds, organizationIds }, {
            headers: {
                "auth-token": token
            }
        });

        return res.data;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error saving companies');
    }
}
);

export const fetchSavedCompanies = createAsyncThunk('savedCompanies/fetchSavedCompanies', async (userId, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const res = await api.get(`/api/savedcompanies/getsavedcompanies/${userId}`, {
            headers: {
                "auth-token": token
            }
        });

        return res.data;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error fetching saved companies');
    }
}
);

// Persist a saved company/signal's pipeline stage.
export const updateSavedCompanyStage = createAsyncThunk('savedCompanies/updateSavedCompanyStage', async ({ savedCompanyId, stage }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const res = await api.patch(`/api/savedcompanies/stage/${savedCompanyId}`, { stage }, {
            headers: { "auth-token": token }
        });
        return res.data?.data || { _id: savedCompanyId, stage };
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to update signal stage');
    }
});

export const deleteSavedCompany = createAsyncThunk('savedCompanies/deleteSavedCompany', async (savedCompanyId, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        await api.delete(`/api/savedcompanies/deletesavedcompany/${savedCompanyId}`, {
            headers: {
                "auth-token": token
            }
        });

        return savedCompanyId;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error deleting saved company');
    }
}
);

// checkCompanyIsSaved removed — route /is-saved never existed.
// The saved state is checked client-side via savedCompanies.some(c => c.companyId === companyId).

const savedCompaniesSlice = createSlice({
    name: 'savedCompanies',
    initialState: {
        savedCompanies: [],
        loading: false,
        error: null,
        isCompanySaved: false,
    },
    reducers: {
        resetSavedCompaniesState: (state) => {
            state.loading = false;
            state.error = null;
            state.isCompanySaved = false;
        },
        setDemoData: (state, action) => {
            state.savedCompanies = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(saveCompany.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveCompany.fulfilled, (state, action) => {
                state.loading = false;
                state.savedCompanies.push(action.payload);
            })
            .addCase(saveCompany.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(saveMultipleCompanies.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveMultipleCompanies.fulfilled, (state) => {
                state.loading = false;
                // Backend returns { message: "Companies saved" } — no array of docs.
                // The component should dispatch fetchSavedCompanies after this succeeds
                // to get fully populated company documents into the store.
            })
            .addCase(saveMultipleCompanies.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchSavedCompanies.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSavedCompanies.fulfilled, (state, action) => {
                state.loading = false;
                state.savedCompanies = action.payload;
            })
            .addCase(fetchSavedCompanies.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(deleteSavedCompany.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSavedCompany.fulfilled, (state, action) => {
                state.loading = false;
                state.savedCompanies = state.savedCompanies.filter(company => company._id !== action.payload);
            })
            .addCase(deleteSavedCompany.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateSavedCompanyStage.fulfilled, (state, action) => {
                const { _id, stage } = action.payload || {};
                const entry = state.savedCompanies.find(c => String(c._id) === String(_id));
                if (entry) entry.stage = stage;
            })

            // checkCompanyIsSaved cases removed — thunk was calling a non-existent /is-saved route.
    }
});

export const { resetSavedCompaniesState } = savedCompaniesSlice.actions;

export default savedCompaniesSlice.reducer;
